// /src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Outlet, // nested route outlet
  useLocation, // to detect nested routes
} from "react-router-dom";

// --- Import shims ---
import HeaderDefault from "./components/Header";
import SidebarDefault from "./components/Sidebar";

// --- Import modern components ---
import ModernHeader from "./components/modern/ModernHeader";
import ModernSidebar from "./components/modern/ModernSidebar";

// --- Import context and UI components ---
import { UIVersionProvider, useUIVersion } from "./contexts/UIVersionContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ToastDisplay } from "./components/Toast";
import UI20SettingsPanel from "./components/UI20SettingsPanel";

// --- Import AI Chatbot ---
import { User } from "lucide-react";
import {
  ExpandableChat,
  ComingSoonChat,
} from "./components/ui/expandable-chat";

// Pages / Modules
import ModernFrontLanding from "./pages/ModernFrontLanding"; // Home (Free/New Agent)
import InsightCenter from "./pages/Education/InsightCenter";
import TrainingCenter from "./pages/Education/TrainingCenter";
import FileVault from "./pages/Education/FileVault";
import OnboardingGame from "./pages/OnboardingGame";

// NEW PAGES
import Business from "./pages/Education/Business";
import Education from "./pages/Education/Education";
import PublicInsightCenter from "./pages/publicInsightCenter";

// ✅ Calendar page (you placed it in ./pages)
import CalendarPage from "./pages/Calendar/CalendarPage";
import MatchupPage from "./pages/MatchupPage";
import TrainingSchedule from "./pages/TrainingSchedule";

// Apps
import TenSystematicTools from "./pages/Apps/TenSystematicTools";

// My Team
import OrgChart from "./pages/OrgChart";
import Tracker4x4Default, {
  default as _t4x4Default,
} from "./pages/MyTeam/Tracker4x4";
import AssociateTrackerDefault, {
  default as _assocDefault,
} from "./pages/MyTeam/AssociateTracker";
import LicensingTrackerDefault, {
  default as _licDefault,
} from "./pages/MyTeam/LicensingTracker";
import ProductionTrackerDefault, {
  default as _prodDefault,
} from "./pages/MyTeam/ProductionTracker";
import ProspectTrackerDefault, {
  default as _prospectDefault,
} from "./pages/MyTeam/ProspectTracker";

// Free Trackers (read-only versions)
import FreeProspectTracker from "./pages/MyTeam/FreeTrackers/FreeProspectTracker";
import FreeProductionTracker from "./pages/MyTeam/FreeTrackers/FreeProductionTracker";
import FreeLicensingTracker from "./pages/MyTeam/FreeTrackers/FreeLicensingTracker";

// Licensing dropdown pages
import TrackMyLicense from "./pages/Licensing/TrackMyLicense";
import CrashCourse from "./pages/Licensing/CrashCourse";
import LicensingDocuments from "./pages/Licensing/LicensingDocuments";
import ChapterCourse from "./pages/Licensing/ChapterCourse"; // player page

// Big Event dropdown pages
import BigEventBuilder from "./pages/BigEvent/BigEventBuilder";
import Purchases from "./pages/BigEvent/Purchases";
import CheckIn from "./pages/BigEvent/CheckIn";
import Permissions from "./pages/BigEvent/Permissions";
import RecognitionOrders from "./pages/BigEvent/RecognitionOrders";
import EventReports from "./pages/BigEvent/EventReports";
import EditEvent from "./pages/BigEvent/EditEvent"; // hidden page opened from Builder

// Auth
import Login from "./pages/Auth/Login";
import SetupProfile from "./pages/Auth/SetupProfile";
import { AuthProvider, useAuth } from "./hooks/useAuth";

// Profile
import Profile from "./pages/Profile/Profile";

// ✅ NEW: Admin Pages
import AdminPage from "./pages/AdminPage";
import UserManagement from "./pages/Admin/UserManagement";
import VideoManagement from "./pages/Admin/VideoManagement";
import PageBuilder from "./pages/Admin/PageBuilder";
import SiteSettings from "./pages/Admin/SiteSettings";
import AnalyticsDashboard from "./pages/Admin/AnalyticsDashboard";
import EventSignup from "./pages/EventSignup";
import OnboardingVideoManager from "./pages/Admin/OnboardingVideoManager";
import UpgradeRequests from "./pages/Admin/UpgradeRequests";
import CarouselManager from "./pages/Admin/CarouselManager";
// OrgChart data prefetch
import orgChartService from "./services/orgChartService";
import UnifiedVideoManager from "./pages/Admin/UnifiedVideoManager";
import { useAccountType } from "./hooks/useAccountType";

// Components selected based on UI version context
const Tracker4x4 = Tracker4x4Default || _t4x4Default;
const AssociateTracker = AssociateTrackerDefault || _assocDefault;
const LicensingTracker = LicensingTrackerDefault || _licDefault;
const ProductionTracker = ProductionTrackerDefault || _prodDefault;
const ProspectTracker = ProspectTrackerDefault || _prospectDefault;

// Role constants and helpers
const ROLE = {
  NEW_AGENT: "New Agent",
  AGENT: "Agent",
  LEADER: "Leader",
  BROKER: "Broker",
  ADMIN: "Admin",
  SENIOR_BROKER: "Senior Broker",
};

// Capitalize role helper to handle legacy lowercase roles
const capitalizeRole = (role) => {
  if (!role) return ROLE.NEW_AGENT;
  
  const lowerRole = role.toLowerCase();
  const mapping = {
    "new agent": ROLE.NEW_AGENT,
    "agent": ROLE.AGENT,
    "leader": ROLE.LEADER,
    "broker": ROLE.BROKER,
    "senior broker": ROLE.SENIOR_BROKER,
    "admin": ROLE.ADMIN,
  };
  
  return mapping[lowerRole] || role;
};

const getRole = () => {
  const rawRole = localStorage.getItem("wb.plan") || ROLE.NEW_AGENT;
  const normalizedRole = capitalizeRole(rawRole);
  
  // Update localStorage if it was lowercase
  if (rawRole !== normalizedRole) {
    localStorage.setItem("wb.plan", normalizedRole);
  }
  
  return normalizedRole;
};

const BIGEVENT_PAGES = new Set([
  "Big Event Builder",
  "Purchases",
  "Check-in",
  "Permissions",
  "Recognition Orders",
  "Reports",
  "Edit Event",
]);

const MYTEAM_PAGES = new Set([
  "Org Chart",
  "Prospect Tracker",
  "4x4 Tracker",
  "Licensing Tracker",
  "Production Tracker",
  "Associate Tracker",
]);

const ADMIN_PAGES = new Set([
  "Admin",
  "User Management",
  "Unified Video Manager",
  "Video Management",
  "Carousel Manager",
  "Page Builder (Beta)",
  "Site Settings",
  "Analytics Dashboard",
  "Event Signup",
  "Onboarding Video Manager",
  "Upgrade Requests",
]);

const LICENSING_FULL = {
  label: "Licensing",
  items: ["Track My License", "Crash Course", "Licensing Documents"],
};

const LICENSING_LITE = {
  label: "Licensing",
  items: ["Track My License", "Crash Course"],
};

const MYTEAM_LITE = {
  label: "My Team",
  items: ["Prospect Tracker"],
};

const MYTEAM_AGENT = {
  label: "My Team",
  items: ["Prospect Tracker", "Org Chart", "Production Tracker"],
};

const MYTEAM_FULL = {
  label: "My Team",
  items: [
    "Org Chart",
    "Prospect Tracker",
    "4x4 Tracker",
    "Associate Tracker",
    "Licensing Tracker",
    "Production Tracker",
  ],
};

const BIG_EVENT_MENU = {
  label: "Big Event",
  items: [
    "Big Event Builder",
    "Purchases",
    "Check-in",
    "Permissions",
    "Recognition Orders",
    "Reports",
  ],
};

const ADMIN_MENU = {
  label: "Admin",
  items: [
    "User Management",
    "Unified Video Manager",
    "Video Management",
    "Carousel Manager",
    "Page Builder (Beta)",
    "Site Settings",
    "Analytics Dashboard",
    "Event Signup",
    "Onboarding Video Manager",
    "Upgrade Requests",
  ],
};

const COMMON_CORE = ["Home", "Insight Center", "Onboarding Game"];

const getSidebarStructure = (role) => {
  switch (role) {
    case ROLE.NEW_AGENT:
      return [
        ...COMMON_CORE,
        LICENSING_LITE,
        "10 Systematic Tools",
        MYTEAM_LITE,
        "Training Center",
        "Training Schedule",
        "File Vault",
        "Help Desk",
      ];
    case ROLE.AGENT:
      return [
        ...COMMON_CORE,
        LICENSING_FULL,
        "10 Systematic Tools",
        MYTEAM_AGENT,
        "Matchup",
        "Training Center",
        "Training Schedule",
        "Calendar",
        "File Vault",
        "Help Desk",
      ];
    case ROLE.LEADER:
      return [
        ...COMMON_CORE,
        LICENSING_FULL,
        "10 Systematic Tools",
        MYTEAM_FULL,
        "Matchup",
        "Training Center",
        "Training Schedule",
        "Calendar",
        "File Vault",
        "Help Desk",
      ];
    case ROLE.BROKER:
    case ROLE.SENIOR_BROKER:
      return [
        ...COMMON_CORE,
        LICENSING_FULL,
        "10 Systematic Tools",
        MYTEAM_FULL,
        "Matchup",
        BIG_EVENT_MENU,
        "Training Center",
        "Training Schedule",
        "Calendar",
        "File Vault",
        "Help Desk",
      ];
    case ROLE.ADMIN:
    default:
      return [
        ...COMMON_CORE,
        LICENSING_FULL,
        "10 Systematic Tools",
        MYTEAM_FULL,
        "Matchup",
        BIG_EVENT_MENU,
        "Training Center",
        "Training Schedule",
        "Calendar",
        "File Vault",
        "Help Desk",
        ADMIN_MENU,
      ];
  }
};

const flattenMenuItems = (structure) => {
  const allowed = new Set();
  structure.forEach((item) => {
    if (typeof item === "string") {
      allowed.add(item);
      return;
    }
    if (item?.items) {
      item.items.forEach((child) => {
        if (typeof child === "string") {
          allowed.add(child);
        } else if (child?.value) {
          allowed.add(child.value);
        } else if (child?.label) {
          allowed.add(child.label);
        }
      });
    }
  });
  return allowed;
};

const buildAllowedPages = (structure, role) => {
  const allowed = flattenMenuItems(structure);

  if ([ROLE.BROKER, ROLE.SENIOR_BROKER, ROLE.ADMIN].includes(role)) {
    BIGEVENT_PAGES.forEach((page) => allowed.add(page));
  }

  if (role === ROLE.ADMIN) {
    ADMIN_PAGES.forEach((page) => allowed.add(page));
  }

  return allowed;
};

// RequireAuth component
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null; // or a tiny spinner if you prefer
  if (!user)
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

// RequireAdmin component
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const role = getRole();
  
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (role !== ROLE.ADMIN) {
    return (
      <div className="content-stage">
        <div className="stage-text">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page. Admin access required.</p>
        </div>
      </div>
    );
  }
  return children;
}

// Header wrapper that selects UI version
function HeaderWithAuth(props) {
  const { useModernUI } = useUIVersion();
  const HeaderComponent = useModernUI ? ModernHeader : HeaderDefault;
  return <HeaderComponent {...props} />;
}

/** ---------------- CRM Shell ---------------- */
function CRMApp({ initial = "Home" }) {
  const { useModernUI, previewRole, isPreviewMode } = useUIVersion();
  const [active, setActive] = React.useState(initial);
  const location = useLocation();
  const { plan: accountPlan } = useAccountType();

  // Get user role (use preview role if in preview mode, otherwise actual role)
  const actualRole = React.useMemo(
    () => capitalizeRole(accountPlan || getRole()),
    [accountPlan]
  );

  React.useEffect(() => {
    if (accountPlan) {
      localStorage.setItem("wb.plan", capitalizeRole(accountPlan));
    }
  }, [accountPlan]);

  const role = isPreviewMode && previewRole ? previewRole : actualRole;
  const sidebarStructure = React.useMemo(
    () => getSidebarStructure(role),
    [role]
  );
  const allowedPages = React.useMemo(
    () => buildAllowedPages(sidebarStructure, role),
    [sidebarStructure, role]
  );

  // render nested pages (Chapter player + Learn pages) inside content via <Outlet/>
  const isNestedRoute =
    location.pathname.startsWith("/licensing/chapter/") ||
    location.pathname.startsWith("/learn/") ||
    location.pathname.startsWith("/admin/") ||
    location.pathname.startsWith("/profile");

  const [openMenus, setOpenMenus] = React.useState({
    MYTEAM: false,
    LICENSING: false,
    BIGEVENT: false,
    ADMIN: false,
  });

  // Prefetch org chart data on app load (after auth), to warm caches
  React.useEffect(() => {
    let cancelled = false;
    const prefetch = async () => {
      try {
        // Preload levels and baseshop view; results are cached in-memory + localStorage
        await orgChartService.preloadAgentLevels();
        await orgChartService.fetchOrgChartData("baseshop", null);
      } catch (err) {
        console.warn("⚠️ OrgChart prefetch failed", err?.message || err);
      }
    };
    prefetch();
    return () => { cancelled = true; };
  }, []);

  const [collapsed, setCollapsed] = React.useState(false);
  const [ui20SettingsOpen, setUI20SettingsOpen] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    window.navigateToCRM = (name) => {
      // If name starts with "/", treat it as a route path
      if (name.startsWith("/")) {
        navigate(name);
      } else {
        setActive(name);
      }
    };
    return () => {
      if (window.navigateToCRM) delete window.navigateToCRM;
    };
  }, [navigate]);

  const onSelect = async (name) => {
    if (name === "Help Desk") {
      window.open(
        "https://wbhelpdesk.netlify.app",
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    
    // Handle admin routes - just set active, no navigation needed
    if (name === "User Management" || 
        name === "Unified Video Manager" ||
        name === "Video Management" || 
        name === "Carousel Manager" ||
        name === "Page Builder (Beta)" || 
        name === "Site Settings" || 
        name === "Analytics Dashboard" || 
        name === "Event Signup" ||
        name === "Onboarding Video Manager") {
      setActive(name);
      return;
    }
    
    // If we are currently on a nested route (e.g., /learn/*), force a
    // navigation back to the root shell so clicking sidebar items switches
    // content as expected.
    if (isNestedRoute) {
      navigate("/");
    }

    setActive(name);
  };

  const toggleMenu = (key) => setOpenMenus((s) => ({ ...s, [key]: !s[key] }));

  const renderActive = () => {
    const canAccessActive = () => {
      if (allowedPages.has(active)) return true;
      if (active === "Admin") return role === ROLE.ADMIN;
      return false;
    };

    if (!canAccessActive()) {
      if (MYTEAM_PAGES.has(active) || BIGEVENT_PAGES.has(active)) {
        return (
          <div className="content-stage">
            <div className="stage-text">
              <h2>🔒 Upgrade Required</h2>
              <p>The <strong>{active}</strong> page is not available for your current plan.</p>
              <p style={{ marginTop: "10px", fontSize: "14px", opacity: 0.8 }}>
                Contact your leader to upgrade access.
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="content-stage">
          <div className="stage-text">
            <h2>Access Denied</h2>
            <p>You don't have permission to access <strong>{active}</strong>.</p>
          </div>
        </div>
      );
    }

    // Top-level - Show different homepage based on account type
    if (active === "Home") {
      // Free/New Agent accounts see ModernFrontLanding
      // Paid accounts (Agent, Leader, Broker, Admin) see ModernFrontLanding with paid features
      // Both use ModernFrontLanding now, which internally handles paid vs free UI
      return <ModernFrontLanding collapsed={collapsed} />;
    }
    if (active === "Insight Center") return <InsightCenter />;
    if (active === "Training Center") return <TrainingCenter plan={role} />;
    if (active === "Onboarding Game") return <OnboardingGame />;
    if (active === "10 Systematic Tools") return <TenSystematicTools plan={role} />;
    if (active === "File Vault") return <FileVault />;

    // Learn pages (if you decide to show them as direct sidebar items)
    if (active === "Business") return <Business />;
    if (active === "Education") return <Education />;

    // Licensing
    if (active === "Track My License") return <TrackMyLicense />;
    if (active === "Crash Course") return <CrashCourse />;
    if (active === "Licensing Documents") return <LicensingDocuments />;

    // My Team
    if (active === "Org Chart") return <OrgChart />;
    if (active === "Prospect Tracker") return <ProspectTracker />;
    if (active === "4x4 Tracker") return <Tracker4x4 />;
    if (active === "Licensing Tracker") return <LicensingTracker />;
    if (active === "Production Tracker") return <ProductionTracker />;
    if (active === "Associate Tracker") return <AssociateTracker />;

    // Big Event
    if (active === "Big Event Builder") return <BigEventBuilder />;
    if (active === "Purchases") return <Purchases />;
    if (active === "Check-in") return <CheckIn />;
    if (active === "Permissions") return <Permissions />;
    if (active === "Recognition Orders") return <RecognitionOrders />;
    if (active === "Reports") return <EventReports />;
    if (active === "Edit Event") return <EditEvent />;

    // Training Schedule
    if (active === "Training Schedule") return <TrainingSchedule />;

    // ✅ Calendar now renders the real page
    if (active === "Calendar") return <CalendarPage />;
    if (active === "Matchup") return <MatchupPage />;

    // ✅ Admin pages
    if (active === "Admin") return <AdminPage />;
    if (active === "User Management") return <UserManagement />;
    if (active === "Unified Video Manager") return <UnifiedVideoManager />;
    if (active === "Video Management") return <VideoManagement />;
    if (active === "Carousel Manager") return <CarouselManager />;
    if (active === "Page Builder (Beta)") return <PageBuilder />;
    if (active === "Site Settings") return <SiteSettings />;
    if (active === "Analytics Dashboard") return <AnalyticsDashboard />;
    if (active === "Event Signup") return <EventSignup />;
    if (active === "Onboarding Video Manager") return <OnboardingVideoManager />;
    if (active === "Upgrade Requests") return <UpgradeRequests />;

    return (
      <div className="content-stage">
        <p className="stage-text">
          This is the <strong>{active}</strong> workspace.
        </p>
      </div>
    );
  };

  const SidebarComponent = useModernUI ? ModernSidebar : SidebarDefault;

  return (
    <div
      className={`app ${collapsed ? "sidebar-collapsed" : ""} ${
        useModernUI ? "modern-ui" : "legacy-ui"
      }`}
    >
      <div className="crm-shell">
        <HeaderWithAuth onUI20SettingsClick={() => setUI20SettingsOpen(true)} />
        <div className={`crm-body ${useModernUI ? "modern-layout" : ""}`}>
          <SidebarComponent
            collapsed={collapsed}
            structure={sidebarStructure}
            active={active}
            onSelect={onSelect}
            openMenus={openMenus}
            onToggle={toggleMenu}
            onCollapseToggle={() => setCollapsed((c) => !c)}
          />
          <main
            className={`crm-content ${useModernUI ? "modern-content" : ""}`}
          >
            <div className="content-card">
              {/* Render nested learn/chapter pages here */}
              {isNestedRoute ? <Outlet /> : renderActive()}
            </div>
          </main>
        </div>
      </div>
      <UI20SettingsPanel
        isOpen={ui20SettingsOpen}
        onClose={() => setUI20SettingsOpen(false)}
      />
    </div>
  );
}

/** Root router */
export default function App() {
  return (
    <UIVersionProvider>
      <AuthProvider>
        <ToastProvider>
          <ToastDisplay />
          <Router>
            <div style={{ position: "relative" }}>
              <Routes>
                {/* Public */}
                <Route path="/login" element={<Login />} />
                <Route path="/setup-profile" element={<SetupProfile />} />
                <Route path="/public-insight-center" element={<PublicInsightCenter />} />
                <Route path="/event-signup" element={<EventSignup />} />
                <Route path="/learn/public-business" element={<Business hideButton={true} />} />
                <Route path="/learn/public-education" element={<Education hideButton={true} />} />

                {/* Free Trackers (read-only) - Protected */}
                <Route
                  path="/prospect-tracker-free"
                  element={
                    <RequireAuth>
                      <FreeProspectTracker />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/production-tracker-free"
                  element={
                    <RequireAuth>
                      <FreeProductionTracker />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/licensing-tracker-free"
                  element={
                    <RequireAuth>
                      <FreeLicensingTracker />
                    </RequireAuth>
                  }
                />

                {/* Protected + nested inside CRM shell */}
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <CRMApp />
                    </RequireAuth>
                  }
                >
                  {/* Chapter player renders INSIDE the shell */}
                  <Route
                    path="licensing/chapter/:chapterId"
                    element={<ChapterCourse />}
                  />

                  {/* Learn pages also render INSIDE the shell (keeps header + sidebar) */}
                  <Route path="learn/business" element={<Business />} />
                  <Route path="learn/education" element={<Education />} />

                  {/* Profile page */}
                  <Route path="profile" element={<Profile />} />

                  {/* Admin routes - protected by admin role */}
                  <Route 
                    path="admin/users" 
                    element={
                      <RequireAdmin>
                        <UserManagement />
                      </RequireAdmin>
                    } 
                  />
                  <Route 
                    path="admin/videos" 
                    element={
                      <RequireAdmin>
                        <VideoManagement />
                      </RequireAdmin>
                    } 
                  />
                  <Route 
                    path="admin/page-builder" 
                    element={
                      <RequireAdmin>
                        <PageBuilder />
                      </RequireAdmin>
                    } 
                  />
                  <Route 
                    path="admin/settings" 
                    element={
                      <RequireAdmin>
                        <SiteSettings />
                      </RequireAdmin>
                    } 
                  />
                  <Route 
                    path="admin/analytics" 
                    element={
                      <RequireAdmin>
                        <AnalyticsDashboard />
                      </RequireAdmin>
                    } 
                  />
                  <Route 
                    path="admin/onboarding-video-manager" 
                    element={
                      <RequireAdmin>
                        <OnboardingVideoManager />
                      </RequireAdmin>
                    } 
                  />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Global AI Chatbot - Available on all pages */}
              <ExpandableChat
                position="bottom-right"
                size="md"
                icon={<User className="h-6 w-6" />}
              >
                <ComingSoonChat currentPage={window.location.pathname} />
              </ExpandableChat>
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </UIVersionProvider>
  );
}